// Single source of truth for linking a lockbox to a listing.
//
// The link lives in TWO records that must agree:
//   - the lockbox doc: status / assignedDealId / assignedDealLabel
//   - the deal doc:    selectedLockbox / lockboxType
// The listing page already wrote both. The inventory historically wrote
// neither (no picker), which is how a lockbox could read "Assigned · 810
// Randolph" in the inventory while the listing showed nothing. These helpers
// write BOTH sides from anywhere, so the two views stay in sync.
import {
  getDealById,
  updateDealById,
  updateElectronicLockboxById,
  updatePushButtonLockboxById,
} from "./firebasefunctions";

const updaterFor = (lockboxType) =>
  lockboxType === "Push Button"
    ? updatePushButtonLockboxById
    : updateElectronicLockboxById;

const dealLabel = (deal) =>
  deal?.propertyaddress || deal?.propertyName || deal?.address || "N/A";

// Free a lockbox by id without trusting a (possibly stale/empty) lockboxType:
// try the likely collection first, then the other, since an id lives in exactly
// one of them. Best-effort — a failure here never undoes the primary link.
const freeLockboxById = async (id, type) => {
  const payload = { status: "Available", assignedDealId: "", assignedDealLabel: "" };
  const primary =
    type === "Push Button" ? updatePushButtonLockboxById : updateElectronicLockboxById;
  const secondary =
    type === "Push Button" ? updateElectronicLockboxById : updatePushButtonLockboxById;
  try {
    await primary(String(id), payload);
  } catch (_) {
    try {
      await secondary(String(id), payload);
    } catch (e) {
      console.error("Free prev lockbox failed (both collections):", id, e);
    }
  }
};

// Assign `lockbox` (of `lockboxType`) to `deal`. Stamps the primary link FIRST
// (both sides) so a later cleanup failure can never leave the new assignment
// half-written, then frees the two prior links so nothing is double-booked.
export async function assignLockboxToDeal({ lockbox, lockboxType, deal }) {
  if (!lockbox?.id || !deal?.createdat) return false;
  const dealId = deal.createdat;
  const newLockboxId = String(lockbox.id);
  const prevDealOnLockbox = lockbox.assignedDealId; // deal this lockbox was on

  // Read the deal FRESH so we free its CURRENT lockbox — the inventory picker's
  // deal objects are loaded once and can lag after earlier assignments in the
  // same session (a stale snapshot would orphan the lockbox assigned in between).
  let current = deal;
  try {
    const fresh = await getDealById(String(dealId));
    if (fresh) current = { ...deal, ...fresh };
  } catch (_) {
    /* fall back to the passed deal */
  }
  const prevLockboxOnDeal = current.selectedLockbox; // lockbox this deal had
  const prevLockboxType = current.lockboxType;

  const update = updaterFor(lockboxType);

  // 1. Primary link, both sides, FIRST.
  await update(newLockboxId, {
    status: "Assigned",
    assignedDealId: dealId,
    assignedDealLabel: dealLabel(current),
  });
  await updateDealById(String(dealId), {
    selectedLockbox: newLockboxId,
    lockboxType,
  });

  // 2. Free the deal's PREVIOUS lockbox (if it was a different one).
  if (prevLockboxOnDeal && String(prevLockboxOnDeal) !== newLockboxId) {
    await freeLockboxById(prevLockboxOnDeal, prevLockboxType);
  }

  // 3. Detach the new lockbox from its PREVIOUS deal (if different).
  if (prevDealOnLockbox && String(prevDealOnLockbox) !== String(dealId)) {
    await updateDealById(String(prevDealOnLockbox), {
      selectedLockbox: "",
      lockboxType: "",
    }).catch((e) => console.error("Detach prev deal failed:", e));
  }

  return true;
}

// Remove the link from both sides. `deal` is optional. The deal side is cleared
// only when that deal genuinely still points at this lockbox, so we never wipe
// a link the deal has since moved to a different lockbox.
export async function unassignLockboxFromDeal({ lockbox, lockboxType, deal }) {
  if (!lockbox?.id) return false;

  await updaterFor(lockboxType)(String(lockbox.id), {
    status: "Available",
    assignedDealId: "",
    assignedDealLabel: "",
  });

  if (deal?.createdat) {
    // Caller named the deal — clear it.
    await updateDealById(String(deal.createdat), {
      selectedLockbox: "",
      lockboxType: "",
    }).catch(() => {});
  } else if (lockbox.assignedDealId) {
    // Fall back to the lockbox's recorded deal, but only clear it if it still
    // actually points at THIS lockbox.
    try {
      const d = await getDealById(String(lockbox.assignedDealId));
      if (d && String(d.selectedLockbox || "") === String(lockbox.id)) {
        await updateDealById(String(lockbox.assignedDealId), {
          selectedLockbox: "",
          lockboxType: "",
        });
      }
    } catch (e) {
      console.error("Unassign deal-side verify failed:", e);
    }
  }

  return true;
}
